/* @flow */
import Typenames from './Typenames'
import { ActionTypes, store } from 'store'
import { Auth } from 'services'
import HomeCustomization from '../components/Home/Customization'
import TwoFactorAuthorization from '../components/Users/Preferences/TwoFactorAuthorization'
import ProfilePicture from '../components/Users/Preferences/ProfilePicture'
import _ from 'lodash'

export type Preferences = {
  autoSave?: boolean,
  snippetCompletion?: boolean,
  typeCompletion?: boolean,
  autoAddAppGroup?: boolean,
  language?: string,
  preferDarkEditor?: string,
  landingPage?: Object,
  '2fa'?: Object,
  profilePicture?: String
}

export type UserActivity = {
  edits?: Array<Edit>
}

export type Edit = {
  at: Number,
  type: String,
  id: String,
  name: String,
  routeParams: Object
}

/**
 * User: users can be in groups.
 * The types contained in this file should not be changed lightly as they are stored on the CMR (kwanza) and user by other software (e.g. Manatee) which expects them to look a certain way.
 * @typedef { Object } User
 * @memberof UserService
 */
export type User = {
  id?: string,
  name: string,
  fullname?: string,
  email?: string,
  password?: string,
  super?: boolean,
  version?: string,
  preferences?: Preferences,
  activity?: UserActivity
}

/**
 * User typename
 * @private
 * @memberof UserService
 */
const TYPENAME = Typenames.USER

/**
 * Creates a new user.
 * @function
 * @memberof UserService
 *
 * @return { Promise<string> } The id of the added user.
 */
const createUser = (user: User): Promise<PostResponse> => {
  return store.dispatch(ActionTypes.CREATE, { typename: TYPENAME, item: user })
}

/**
 * Reads a user, flattening 'descriptive'.
 * @function
 * @memberof UserService
 *
 * @return { Promise<User> } The user.
 */
const readUser = (id: string): Promise<User> => {
  return store.dispatch(ActionTypes.GET_BY_ID, { typename: TYPENAME, id: id })
}

/**
 * Reads a user given a username.
 * @function
 * @memberof UserService
 *
 * @return { Promise<Array<User>> } The user with the given username.
 */
const byName = (userName: string): Promise<Array<User>> => {
  return store.dispatch(ActionTypes.GET_BY_NAME, { typename: TYPENAME, name: userName })
}

/**
 * Updates a user.
 * @function
 * @memberof UserService
 *
 * @return { Promise<any> }
 */
const updateUser = (id: String, user: User): Promise<PostResponse> => {
  return store.dispatch(ActionTypes.UPDATE, { typename: TYPENAME, id: id, item: user })
}

const currentUser = (): Promise<User | Object> => {
  const username = Auth.getLoggedInUser()
  if (!username) return Promise.resolve({})
  return readAllUsers().then(users => _.cloneDeep(_.find(users, user => user.name === username)))
}

/**
 * Record an edit on the current user
 * @function
 * @memberof UserService
 *
 * @return { Promise<any> }
 */
const addEdit = (edit: Edit): Promise<PostResponse> => {
  return currentUser().then(u => {
    if (!u || _.isEmpty(u)) {
      console.warn('Empty user')
      return Promise.resolve({ id: '', version: '' })
    }

    // we either touch an existing edit or add a new
    var edits = u.activity?.edits || []
    const editIdx = _.findIndex(edits, e => e.type == edit.type && e.id === edit.id)
    if (editIdx >= 0) {
      edits[editIdx] = { ...edits[editIdx], at: Date.now(), ...edit }
    } else {
      edits.push({ at: Date.now(), ...edit })
    }

    // Sort by 'at', take the 100 newest entries
    edits = _.sortBy(edits, 'at')
    _.reverse(edits)
    edits = _.take(edits, 100)

    _.set(u, 'activity.edits', edits)

    // $FlowFixMe
    return updateUser(u.id, u)
  })
}

/**
 * Deletes a user.
 * @function
 * @memberof UserService
 *
 * @return { Promise<any> }
 */
const deleteUser = (id: string): Promise<any> => {
  return store.dispatch(ActionTypes.DELETE, { typename: TYPENAME, id: id })
}

/**
 * Reads all users, flattening descriptive.
 * @function
 * @memberof UserService
 *
 * @return { Promise<Array<User>> } All the users.
 */
const readAllUsers = (): Promise<Array<User>> => {
  return store.dispatch(ActionTypes.GET, TYPENAME)
}

export const preferenceCategories = {
  EDITOR: 'PREFERENCES_EDITOR',
  MISC: 'PREFERENCES_MISC',
  LANDINGPAGE: 'PREFERENCES_LANDING_PAGE',
  SECURITY: 'PREFERENCES_SECURITY',
  PROFILEPICTURE: 'PREFERENCES_PROFILE_PICTURE'
}

export const preferenceTypes = {
  BOOL: 'bool',
  ENUM: 'enum'
}

const preferenceDeclarations = [
  {
    name: 'autoSave',
    type: preferenceTypes.BOOL,
    category: preferenceCategories.EDITOR,
    description: 'ENABLE_AUTO_SAVE',
    default: true
  },
  {
    name: 'snippetCompletion',
    type: preferenceTypes.BOOL,
    category: preferenceCategories.EDITOR,
    description: 'USE_SNIPPETS',
    default: false
  },
  {
    name: 'typeCompletion',
    type: preferenceTypes.BOOL,
    category: preferenceCategories.EDITOR,
    description: 'USE_TYPES',
    default: true
  },
  {
    name: 'preferDarkEditor',
    type: preferenceTypes.ENUM,
    category: preferenceCategories.EDITOR,
    description: 'THEME',
    default: 'browser',
    enumValues: () => ['light', 'dark', 'browser'],
    toDisplayValue: (value: string, $i18n: any) =>
      typeof value === 'string' ? $i18n.t('THEME_' + value.toUpperCase()) : value + ''
  },
  {
    name: 'language',
    type: preferenceTypes.ENUM,
    category: preferenceCategories.MISC,
    description: 'LANGUAGE',
    default: 'en',
    enumValues: ($locale: any) => ($locale ? $locale.availableLanguages : ['en']),
    toDisplayValue: (value: string) => value
  },
  {
    name: 'autoAddAppGroup',
    type: preferenceTypes.BOOL,
    category: preferenceCategories.MISC,
    description: 'ENABLE_AUTO_APP_GROUP',
    default: true
  },
  {
    name: 'landingPage',
    component: HomeCustomization,
    category: preferenceCategories.LANDINGPAGE,
    description: 'LANDING_PAGE',
    default: {}
  },
  {
    name: '2fa',
    component: TwoFactorAuthorization,
    category: preferenceCategories.SECURITY,
    description: '2FA',
    default: {},
    dependsOnFeature: '2fa'
  },
  {
    name: 'profilePicture',
    component: ProfilePicture,
    category: preferenceCategories.PROFILEPICTURE,
    description: 'CHANGE_PROFILE_PICTURE',
    default: ''
  }
]

export const getDefaultPreferenceObject = (): Preferences => {
  const res = {}
  for (const pref of preferenceDeclarations) {
    res[pref.name] = pref.default
  }
  // $FlowFixMe
  return res
}

// 'Clone' function values by reference
export const getPreferenceDeclarations = () =>
  _.cloneDeepWith(preferenceDeclarations, value => {
    if (_.isFunction(value)) return value
  })

const self = {
  createUser,
  readUser,
  updateUser,
  deleteUser,
  readAllUsers,
  byName,
  addEdit,
  currentUser
}

/**
 * User service: CRUD interface for users.
 * @module UserService
 * @namespace UserService
 * @version 1.0.0
 */
export default self
